import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../ui/alert-dialog";
import { getDemoStatus, demoReset, removeSampleData } from "../../lib/api";

export default function DemoResetCard() {
  const [status, setStatus] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [resetting, setResetting] = useState(false);
  const [removingSample, setRemovingSample] = useState(false);

  useEffect(() => {
    getDemoStatus().then((res) => setStatus(res.data)).catch(() => {});
  }, []);

  const handleRemoveSample = async () => {
    if (!window.confirm("Remove only the built-in demo/sample staff, students, and school districts? Your own data and admin login are kept.")) return;
    setRemovingSample(true);
    try {
      const res = await removeSampleData();
      const d = res.data.deleted || {};
      toast.success(`${res.data.message} (staff ${d.staff || 0}, students ${d.students || 0}, districts ${d.districts || 0})`);
      getDemoStatus().then((r) => setStatus(r.data)).catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove sample data");
    } finally {
      setRemovingSample(false);
    }
  };

  const handleReset = async () => {
    if (confirmText !== "RESET") {
      toast.error('Type RESET to confirm');
      return;
    }
    setResetting(true);
    try {
      const res = await demoReset({ confirm: "RESET" });
      toast.success(`Reset complete. Deleted records across ${Object.keys(res.data.deleted).length} collections. Redirecting…`);
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally {
      setResetting(false);
    }
  };

  return (
    <Card className="border-red-200 bg-red-50/30" data-testid="demo-reset-card">
      <CardHeader>
        <div className="flex items-center gap-2">
          <CardTitle className="text-red-800">Reset & Start a Real School</CardTitle>
          {status?.looks_like_demo && <Badge className="bg-amber-500">Demo data detected</Badge>}
        </div>
        <CardDescription>
          <strong>⚠️ DESTRUCTIVE.</strong> This wipes all students, staff (except you), attendance, assessments,
          communications, files, proficiency records, and other operational data — giving you a clean slate
          to set up your real school. <strong>Preserved:</strong> the 93 proficiency indicators, your school
          branding, and your admin login.
        </CardDescription>
      </CardHeader>
      {status && (
        <CardContent className="space-y-3">
          <div className="text-sm text-muted-foreground">
            Currently: <strong>{status.student_count}</strong> students · <strong>{status.staff_count}</strong> staff
          </div>
          {status.admin_email && (
            <div className="text-sm bg-green-50 border border-green-200 rounded-lg p-3 text-green-800" data-testid="demo-reset-kept-login">
              ✅ Your login will keep working after reset:{" "}
              <strong>{status.admin_email}</strong> (same password). You won&apos;t be logged out.
            </div>
          )}
          <div className="flex flex-col gap-2 p-3 border rounded-lg bg-white">
            <div className="text-sm">
              <strong>Remove sample data only.</strong> Deletes just the built-in demo staff, students,
              and school districts (the fake records that ship with the app). Your real data and admin login are untouched.
            </div>
            <Button variant="outline" className="self-start" onClick={handleRemoveSample} disabled={removingSample} data-testid="remove-sample-data-btn">
              {removingSample ? "Removing…" : "Remove sample data"}
            </Button>
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" data-testid="demo-reset-trigger">Reset all operational data…</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Wipe all student & staff data?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete all students, attendance, assessments, communications, files,
                  proficiency records, narratives, and risk assessments. The Set-Up Wizard will reopen so you
                  can configure your real school from scratch. <strong>This cannot be undone.</strong>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="space-y-1 py-2">
                <Label className="text-xs">Type <code className="bg-muted px-1">RESET</code> to confirm:</Label>
                <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} data-testid="demo-reset-confirm-input" />
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel onClick={() => setConfirmText("")}>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleReset} disabled={resetting || confirmText !== "RESET"} data-testid="demo-reset-confirm-btn">
                  {resetting ? "Wiping…" : "Yes, wipe everything"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>
      )}
    </Card>
  );
}
